import { supabase } from "@/integrations/supabase/client";

export interface LeasingContract {
  id: string;
  contract_number: string;
  plate_number: string;
  supplier_tax_id: string;
  description: string;
  cost_recovery_account?: string | null;
  financial_charge_account?: string | null;
  insurance_account?: string | null;
}

export interface LeasingScheduleRow {
  id: string;
  contract_id: string;
  installment_number: number;
  due_date: string;
  cost_recovery: number;
  financial_charge: number;
  insurance: number;
  total_amount: number;
}

export interface LeasingMatchResult {
  contractNumber: string;
  plateNumber: string;
  installmentNumber: number;
  costRecovery: number;
  financialCharge: number;
  insurance: number;
  costRecoveryAccount: string;
  financialChargeAccount: string;
  insuranceAccount: string;
  supplierAccountCode: string;
  supplierDescription: string;
}

const hasScheduleInMonth = (schedules: LeasingScheduleRow[], contractId: string, invoiceDate: string): boolean =>
  schedules.some((s) => s.contract_id === contractId && sameYearMonth(s.due_date, invoiceDate));

const scheduleBase = (row: LeasingScheduleRow): number =>
  Number(row.total_amount) || Number(row.cost_recovery) + Number(row.financial_charge) + Number(row.insurance);

const findScheduleInMonth = (schedules: LeasingScheduleRow[], contractId: string, invoiceDate: string): LeasingScheduleRow | undefined =>
  schedules.find((s) => s.contract_id === contractId && sameYearMonth(s.due_date, invoiceDate));

interface CatalogRow {
  account: string;
  description: string;
}

interface SupplierMasterRow {
  account_code: string;
  description: string;
  tax_id?: string;
}

export interface SupplierLeasingDefault {
  supplier_tax_id: string;
  cost_recovery_account: string;
  financial_charge_account: string;
  insurance_account: string;
}

const normalizePlate = (plate: string): string =>
  plate.replace(/[\s\-]/g, "").toUpperCase();

const normalizeText = (t: string): string =>
  t.replace(/[\s\-_.]/g, "").toUpperCase();

const contractDigits = (contract: string): string =>
  String(contract || "").replace(/\D/g, "");

const sameContractNumber = (catalogContract: string, invoiceContract: string): boolean => {
  const catalogNorm = normalizeText(catalogContract);
  const invoiceNorm = normalizeText(invoiceContract);
  if (!catalogNorm || !invoiceNorm) return false;
  if (catalogNorm === invoiceNorm) return true;

  const catalogDigits = contractDigits(catalogContract);
  const invoiceDigits = contractDigits(invoiceContract);
  if (catalogDigits.length < 6 || invoiceDigits.length < 6) return false;

  // Some invoices only print the operation suffix (e.g. "0330785-63") while
  // the table stores the full contract ("9320.50.0330785-63").
  return catalogDigits === invoiceDigits || catalogDigits.endsWith(invoiceDigits) || invoiceDigits.endsWith(catalogDigits);
};

const sameYearMonth = (a: string, b: string): boolean => {
  const left = String(a || "").slice(0, 7);
  const right = String(b || "").slice(0, 7);
  return /^\d{4}-\d{2}$/.test(left) && left === right;
};

/**
 * Pick a 6623* account from the catalog whose description contains a meaningful
 * token from the supplier description (e.g. "DAIMLER", "SCANIA", "IBERICA",
 * "CAIXABANK"). Returns undefined if no match — caller should fall back.
 */
export function pickInterestAccountByDescription(
  catalog: { account: string; description: string }[],
  supplierDescription: string | undefined,
): { account: string } | undefined {
  if (!supplierDescription) return undefined;
  const interestAccounts = catalog.filter((c) => c.account.startsWith("6623"));
  if (interestAccounts.length === 0) return undefined;
  const STOP = new Set([
    "SA", "SL", "SAU", "SLU", "EFC", "ESPANA", "ESPAÑA", "TRUCK", "FINANCIAL",
    "FINANCE", "SERVICES", "SERVICIOS", "EQUIPMENT", "EQUIPRENT", "HISPANIA",
    "RENTING", "LEASING", "BANK", "BANCO", "DE", "LA", "EL", "Y", "DEL",
    "INTERESES", "DEUDA", "PRESTAMO",
  ]);
  const tokens = supplierDescription
    .toUpperCase()
    .split(/[^A-ZÁÉÍÓÚÑ]+/)
    .filter((t) => t.length >= 4 && !STOP.has(t));
  for (const token of tokens) {
    const hit = interestAccounts.find((c) =>
      (c.description || "").toUpperCase().includes(token),
    );
    if (hit) return { account: hit.account };
  }
  return undefined;
}

/**
 * Load all leasing contracts + their schedules for a company.
 * Loads:
 *  - leasing_contracts + leasing_schedule for amounts/installments
 *  - company_account_catalog (Contrapartidas) for the expense account codes (524/6623/625)
 *  - supplier_master only for the supplier account (400x/410x) lookup by tax_id
 */
export async function loadLeasingData(companyId: string) {
  const [contractsRes, catalogRes, masterRes, defaultsRes] = await Promise.all([
    supabase.from("leasing_contracts").select("*").eq("company_id", companyId),
    supabase.from("company_account_catalog").select("account, description").eq("company_id", companyId),
    supabase.from("supplier_master").select("account_code, description, tax_id").eq("company_id", companyId),
    supabase
      .from("supplier_leasing_defaults")
      .select("supplier_tax_id, cost_recovery_account, financial_charge_account, insurance_account")
      .eq("company_id", companyId),
  ]);

  const contracts: LeasingContract[] = (contractsRes.data || []) as LeasingContract[];
  const catalog: CatalogRow[] = (catalogRes.data || []) as CatalogRow[];
  const master: SupplierMasterRow[] = (masterRes.data || []) as SupplierMasterRow[];
  const defaults: SupplierLeasingDefault[] = (defaultsRes.data || []) as SupplierLeasingDefault[];

  if (contracts.length === 0 && defaults.length === 0) return null;

  let schedules: LeasingScheduleRow[] = [];
  if (contracts.length > 0) {
    const contractIds = contracts.map((c) => c.id);
    // Paginar para evitar el límite por defecto de 1000 filas de PostgREST,
    // que truncaría cuotas de contratos con muchos plazos.
    const PAGE = 1000;
    let from = 0;
    while (true) {
      const { data: pageData, error: pageErr } = await supabase
        .from("leasing_schedule")
        .select("*")
        .in("contract_id", contractIds)
        .order("installment_number")
        .range(from, from + PAGE - 1);
      if (pageErr) {
        console.error("Error loading leasing_schedule page:", pageErr);
        break;
      }
      const rows = (pageData || []) as LeasingScheduleRow[];
      schedules.push(...rows);
      if (rows.length < PAGE) break;
      from += PAGE;
    }
  }

  return { contracts, schedules, catalog, master, defaults };
}

/**
 * Normaliza un NIF/CIF: elimina espacios, puntos y guiones, pasa a mayúsculas,
 * quita el prefijo ES y, si el texto trae el NIF dentro de una descripción
 * (p.ej. "CAIXABANK, S.A. (A08663619)"), extrae sólo el identificador.
 */
export const normalizeSupplierNif = (nif: string): string => {
  const cleaned = (nif || "")
    .replace(/[\s.\-,]/g, "")
    .toUpperCase()
    .replace(/^ES(?=[A-Z0-9]{9}$)/, "");
  const m = cleaned.match(/[A-Z]?[0-9]{7,8}[A-Z0-9]/);
  return m ? m[0] : cleaned;
};

const normalizeNifLocal = normalizeSupplierNif;

/**
 * Returns the per-supplier leasing default split (recuperación de coste / carga financiera /
 * seguro) configured for an invoice's supplier in this company, or undefined.
 */
export function lookupLeasingDefault(
  defaults: SupplierLeasingDefault[] | undefined,
  invoiceSupplierTaxId: string | undefined,
): SupplierLeasingDefault | undefined {
  if (!defaults || defaults.length === 0 || !invoiceSupplierTaxId) return undefined;
  const norm = normalizeNifLocal(invoiceSupplierTaxId);
  return defaults.find((d) => normalizeNifLocal(d.supplier_tax_id) === norm);
}

/**
 * Match a processed invoice against leasing contracts.
 * Account codes (524/6623/625) come from the company_account_catalog (Contrapartidas).
 * Supplier account (400x/410x) comes from supplier_master via tax_id.
 */
export function matchLeasing(
  data: {
    contracts: LeasingContract[];
    schedules: LeasingScheduleRow[];
    catalog: CatalogRow[];
    master: SupplierMasterRow[];
    defaults?: SupplierLeasingDefault[];
  },
  contractNumber: string | undefined,
  licensePlate: string | undefined,
  invoiceDate: string,
  invoiceSupplierTaxId: string | undefined,
  invoiceBaseAmount?: number,
): LeasingMatchResult | null {
  const normalizedPlate = licensePlate ? normalizePlate(licensePlate) : "";
  const normalizedContract = (contractNumber || "").trim();
  const normalizedContractText = normalizeText(normalizedContract);
  const normalizedInvoiceTaxId = (invoiceSupplierTaxId || "")
    .replace(/[\s.\-]/g, "")
    .toUpperCase();

  const contractsForSupplier = data.contracts.filter((c) => {
    const contractTaxIdNorm = (c.supplier_tax_id || "")
      .replace(/[\s.\-]/g, "")
      .toUpperCase();
    return contractTaxIdNorm === normalizedInvoiceTaxId;
  });

  // Primary rule: if the invoice has a contract number, match the leasing table
  // by that contract number even if the supplier NIF extraction is imperfect.
  let contract = normalizedContract
    ? (contractsForSupplier.find((c) => sameContractNumber(c.contract_number, normalizedContract)) ||
        data.contracts.find((c) => sameContractNumber(c.contract_number, normalizedContract)))
    : null;

  if (!contract && normalizedPlate) {
    contract =
      contractsForSupplier.find((c) => normalizePlate(c.plate_number) === normalizedPlate) ||
      data.contracts.find((c) => normalizePlate(c.plate_number) === normalizedPlate) ||
      null;
  }

  // Sin nº de contrato ni matrícula coincidentes no se relaciona con ninguna
  // tabla de leasing, aunque el proveedor tenga contratos en esa empresa.


  if (!contract) {
    console.warn("[matchLeasing] NO CONTRACT FOUND", {
      normalizedContract,
      normalizedContractText,
      normalizedPlate,
      normalizedInvoiceTaxId,
      contractsForSupplier: contractsForSupplier.map((c) => ({
        contract: c.contract_number,
        normalized: normalizeText(c.contract_number),
        plate: c.plate_number,
      })),
      sameContractNumberTests: contractsForSupplier.map((c) => ({
        contract: c.contract_number,
        match: sameContractNumber(c.contract_number, normalizedContract),
      })),
    });
    return null;
  }

  const contractSchedules = data.schedules.filter((s) => s.contract_id === contract.id);
  if (contractSchedules.length === 0) {
    console.warn("[matchLeasing] CONTRACT FOUND BUT NO SCHEDULES", {
      contract: contract.contract_number,
      contractId: contract.id,
      totalSchedules: data.schedules.length,
    });
    return null;
  }

  const bestMatch = contractSchedules.find((s) => sameYearMonth(s.due_date, invoiceDate)) || null;

  if (!bestMatch) {
    console.warn("[matchLeasing] CONTRACT FOUND BUT NO SCHEDULE FOR MONTH", {
      contract: contract.contract_number,
      invoiceDate,
      availableDates: contractSchedules.map((s) => s.due_date),
    });
    return null;
  }

  // Account codes from company_account_catalog (Contrapartidas)
  const plateNorm = normalizePlate(contract.plate_number);
  const contractNumNorm = normalizeText(contract.contract_number);

  // 524: amortización / recuperación coste.
  // Prioridad 1: cuenta específica guardada en el propio contrato (override por contrato).
  // Prioridad 2: subcuenta 524 del catálogo cuya descripción contenga el número de contrato.
  // Prioridad 3: fallback por matrícula.
  const contractDigitsAll = contractNumNorm.replace(/\D/g, "");
  const contractOverrideAccount = (contract.cost_recovery_account || "").trim();
  const costRecoveryEntry = contractOverrideAccount
    ? { account: contractOverrideAccount, description: "" }
    : (data.catalog.find((c) => {
        if (!c.account.startsWith("524")) return false;
        const desc = normalizeText(c.description || "");
        if (!desc) return false;
        if (contractNumNorm && desc.includes(contractNumNorm)) return true;
        if (normalizedContractText && desc.includes(normalizedContractText)) return true;
        const descDigits = desc.replace(/\D/g, "");
        if (contractDigitsAll.length >= 4 && descDigits.includes(contractDigitsAll)) return true;
        if (contractDigitsAll.length >= 6 && descDigits.includes(contractDigitsAll.slice(-6))) return true;
        return false;
      }) ||
      data.catalog.find((c) => {
        if (!c.account.startsWith("524")) return false;
        const desc = normalizeText(c.description || "");
        return !!(plateNorm && desc.includes(plateNorm));
      }));


  // Supplier account (400x/410x) by tax_id of the contract from supplier_master
  const contractTaxId = normalizeSupplierNif(contract.supplier_tax_id || "");
  const supplierEntry = contractTaxId
    ? data.master.find((m) => normalizeSupplierNif(m.tax_id || "") === contractTaxId)
    : undefined;

  const supplierDefault = contractTaxId
    ? (data.defaults || []).find((d) => normalizeSupplierNif(d.supplier_tax_id || "") === contractTaxId)
    : undefined;

  // 6623: intereses.
  // Prioridad 1: cuenta guardada en el propio contrato (configurable por el usuario).
  // Prioridad 2: cuenta configurada en supplier_leasing_defaults para el NIF del contrato.
  // Prioridad 3: descripción del proveedor (DAIMLER, SCANIA, etc.).
  const contractFinancialCharge = (contract.financial_charge_account || "").trim();
  const financialChargeEntry = (contractFinancialCharge
    ? { account: contractFinancialCharge }
    : undefined)
    || (supplierDefault?.financial_charge_account
      ? { account: supplierDefault.financial_charge_account }
      : undefined)
    || pickInterestAccountByDescription(
      data.catalog,
      supplierEntry?.description || contract.description,
    );

  // 625: seguro.
  // Prioridad 1: cuenta del contrato → 2: default del proveedor → 3: primera 625 del catálogo.
  const contractInsurance = (contract.insurance_account || "").trim();
  const insuranceEntry = (contractInsurance ? { account: contractInsurance } : undefined)
    || (supplierDefault?.insurance_account ? { account: supplierDefault.insurance_account } : undefined)
    || data.catalog.find((c) => c.account.startsWith("625"));

  return {
    contractNumber: contract.contract_number,
    plateNumber: contract.plate_number,
    installmentNumber: bestMatch.installment_number,
    costRecovery: Number(bestMatch.cost_recovery),
    financialCharge: Number(bestMatch.financial_charge),
    insurance: Number(bestMatch.insurance),
    costRecoveryAccount: costRecoveryEntry?.account || "",
    financialChargeAccount: financialChargeEntry?.account || "",
    insuranceAccount: insuranceEntry?.account || "",
    supplierAccountCode: supplierEntry?.account_code || "",
    supplierDescription: supplierEntry?.description || "",
  };
}
